import type { UserInfo, LoginInfo, IsWeakPassword } from '/#/store';
import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import { PageEnum } from '/@/enums/pageEnum';
import {
  ROLES_KEY,
  TOKEN_KEY,
  USER_INFO_KEY,
  LOGIN_INFO_KEY,
  DB_DICT_DATA_KEY,
  TENANT_ID,
  WEAK_PASSWORD,
  USER_CONFIG_KEY, SERVER_CONFIG_KEY,
  FIELDS_COLUMNS_CONFIG_KEY,
  DRAWER_INFO_KEY,
  ANNOTATION_REVIEW_HINT_KEY,
} from '/@/enums/cacheEnum';
import {getAuthCache, removeAuthCache, setAuthCache} from '/@/utils/auth';
import { GetUserInfoModel, LoginParams,ThirdLoginParams } from '/@/api/sys/model/userModel';
import {
  doLogout, getServerSysConfig,
  getUserConfig, getUserFieldColumnsConfig,
  getUserInfo,
  loginApi,
  phoneLoginApi,
  queryFieldConfigListApi,
  queryTenantDisabledDictItemsApi,
  thirdLogin
} from "/@/api/sys/user";
import { useI18n } from '/@/hooks/web/useI18n';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw } from 'vue-router';
import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { isArray } from '/@/utils/is';
import { useGlobSetting } from '/@/hooks/setting';
import {useSso} from '/@/hooks/web/useSso';
import {SysConfig, UserConfig} from "/#/store";
import { TenantTypeEnum } from '/@/enums/tenantTypeEnum';
import { trackOnlineTimeBuryPoint } from '/@/logics/buryPoint';
import { useNotificationStore } from '/@/store/modules/notification';
import {setLocalStorageItem,getLocalStorageItem, setSessionStorage, getSessionStorage, getUrlParam} from "/@/utils/index";
interface UserState {
  userInfo: Nullable<UserInfo>;
  token?: string;
  roleList: RoleEnum[];
  dictItems?: [];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  tenantid?: string|number;
  loginInfo?: Nullable<LoginInfo>;
  isWeakPassword: IsWeakPassword;
  userConfig: Nullable<UserConfig>;
  serverSysConfig: Nullable<SysConfig>;
  fieldsColumnsConfig?: {}
  drawerInfo?: {},  // 开票人信息
  tenantDisabledDictItems?: []
  tenantFieldConfigList?: []
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // 用户信息
    userInfo: null,
    // token
    token: undefined,
    // 角色列表
    roleList: [],
    // 字典
    dictItems: [],
    // session过期时间
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    //租户id
    tenantid: '',
    //登录返回信息
    loginInfo: null,
    // 是否是弱密码
    isWeakPassword: {
      value: getAuthCache<boolean>(WEAK_PASSWORD)
    },
    userConfig:null,
    serverSysConfig:null,
    fieldsColumnsConfig: {},
    drawerInfo: null, 
    tenantDisabledDictItems: [],
    tenantFieldConfigList: [],
  }),
  getters: {
    getUserInfo(): UserInfo {
      // @ts-ignore
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getLoginInfo(): LoginInfo {
      return this.loginInfo || getAuthCache<LoginInfo>(LOGIN_INFO_KEY) || {};
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getAllDictItems(): []{
      return this.dictItems || getAuthCache(DB_DICT_DATA_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
    getTenant(): string|number{
      return this.tenantid || getAuthCache<string|number>(TENANT_ID);
    },
    getIsWeakPassword(): IsWeakPassword {
      return this.isWeakPassword;
    },
    getUserConfig(): UserConfig {
      // @ts-ignore
      // return this.userConfig || getAuthCache<UserConfig>(USER_CONFIG_KEY) || {};
      return this.userConfig || getLocalStorageItem(USER_CONFIG_KEY) || {};
    },
    getServerSysConfig(): SysConfig {
      // @ts-ignore
      // return this.serverSysConfig || getAuthCache<SysConfig>(SERVER_CONFIG_KEY) || {};
      return this.serverSysConfig || getLocalStorageItem(SERVER_CONFIG_KEY) || {};
    },
    getUserFieldsColumnsConfig() {
      // @ts-ignore
      // return this.fieldsColumnsConfig || getAuthCache(FIELDS_COLUMNS_CONFIG_KEY) || {}
      return this.fieldsColumnsConfig || getLocalStorageItem(FIELDS_COLUMNS_CONFIG_KEY) || {}
    },
    isIndustrialUser() {
      return this.getUserInfo.tenantType === TenantTypeEnum.INDUSTRIAL;
    },
    getDrawerInfo(): any {
      return this.drawerInfo || getSessionStorage(DRAWER_INFO_KEY) || {}
    },
    isCsoUser() {
      return this.getUserInfo.tenantType === TenantTypeEnum.CSO;
    },
    isOutsourcerUser() {
      return this.getUserInfo.tenantType === TenantTypeEnum.OUTSOURCER;
    },
    getTenantDisabledDictItems() {
      // @ts-ignore
      return this.tenantDisabledDictItems || [];
    },
    getTenantFieldConfigList() {
      // @ts-ignore
      return this.tenantFieldConfigList || [];
    }
  },
  actions: {
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      setAuthCache(USER_INFO_KEY, info);
    },
    setLoginInfo(info: LoginInfo | null) {
      this.loginInfo = info;
      if(info?.sysAllDictItems){
       delete info.sysAllDictItems
      }
      // console.log(info,'loginInfo')
      setAuthCache(LOGIN_INFO_KEY, info);
    },  
    setAllDictItems(dictItems) {
      this.dictItems = dictItems;
      setAuthCache(DB_DICT_DATA_KEY, dictItems);
    },
    setTenant(id){
      this.tenantid = id;
      setAuthCache(TENANT_ID, id);
      //update-begin---author:liusq  Date:20220102  for：保存用户租户id----
      // @ts-ignore
      setAuthCache(this.userInfo.username, id);
      //update-end---author:liusq  Date:20220102  for：保存用户租户id----
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.dictItems = [];
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
    },
    setIsWeakPassword(value: boolean) {
      console.log(value)
      setAuthCache(WEAK_PASSWORD, value);
      this.isWeakPassword = {
        value
      };
    },
    setUserConfig(config: UserConfig | null) {
      this.userConfig = config;
      // setAuthCache(USER_CONFIG_KEY, config);
      removeAuthCache(USER_CONFIG_KEY)
      setLocalStorageItem(USER_CONFIG_KEY,config)
    },
    setServerSysConfig(config) {
      this.serverSysConfig = config;
      // @ts-ignore
      // setAuthCache(SERVER_CONFIG_KEY, config);
      removeAuthCache(SERVER_CONFIG_KEY)
      setLocalStorageItem(SERVER_CONFIG_KEY,config)
    },
    setFieldsColumnsConfig(data) {
      this.fieldsColumnsConfig = data;
      // setAuthCache(FIELDS_COLUMNS_CONFIG_KEY, data);
      removeAuthCache(FIELDS_COLUMNS_CONFIG_KEY)
      setLocalStorageItem(FIELDS_COLUMNS_CONFIG_KEY,data)
    },
    setTenantDisabledDictItems(data) {
      this.tenantDisabledDictItems = data;
    },
    setTenantFieldConfigList(data) {
      this.tenantFieldConfigList = data;
    },
    setDrawerInfo(data) {
      this.drawerInfo = data;
      setSessionStorage(DRAWER_INFO_KEY,data)
    },
    /**
     * 登录事件
     */
    async login(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await loginApi(loginParams, mode);
        const { token } = data;
        // save token
        this.setToken(token);
        return this.afterLoginAction(goHome,data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    /**
     * 扫码登录事件
     */
    async qrCodeLogin(token): Promise<GetUserInfoModel | null> {
      try {
        // save token
        this.setToken(token);
        return this.afterLoginAction(true,{});
      } catch (error) {
        return Promise.reject(error);
      }
    },
    /**
     * 登录完成处理
     * @param goHome
     */
    async afterLoginAction(goHome?: boolean,data?:any): Promise<any | null> {
      if (!this.getToken) return null;
      //获取用户信息
      const userInfo = await this.getUserInfoAction();
      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        // const permissionStore = usePermissionStore();
        // if (!permissionStore.isDynamicAddedRoute) {
        //   const routes = await permissionStore.buildRoutesAction();
        //   routes.forEach((route) => {
        //     router.addRoute(route as unknown as RouteRecordRaw);
        //   });
        //   router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
        //   permissionStore.setDynamicAddedRoute(true);
        // }
        const notificationStore = useNotificationStore();
        notificationStore.setMsgIsFirstLoad(true);
        await this.setLoginInfo(data);
        let redirect = router.currentRoute.value?.query?.redirect as string;
        // 判断是否有 redirect 重定向地址
        //update-begin---author:wangshuai ---date:20230424  for：【QQYUN-5195】登录之后直接刷新页面导致没有进入创建组织页面------------
        if (redirect && goHome) {
          //update-end---author:wangshuai ---date:20230424  for：【QQYUN-5195】登录之后直接刷新页面导致没有进入创建组织页面------------
          // update-begin--author:liaozhiyang---date:20240104---for：【QQYUN-7804】部署生产环境，登录跳转404问题
          let publicPath = import.meta.env.VITE_PUBLIC_PATH;
          if (publicPath && publicPath != '/') {
            // update-begin--author:liaozhiyang---date:20240509---for：【issues/1147】登录跳转时去掉发布路径的最后一个/以解决404问题
            if (publicPath.endsWith('/')) {
              publicPath = publicPath.slice(0, -1);
            }
            redirect = publicPath + redirect;
          }
          // update-end--author:liaozhiyang---date:20240509---for：【issues/1147】登录跳转时去掉发布路径的最后一个/以解决404问题
          // 当前页面打开
          window.open(redirect, '_self')
          return data;
        }
        let ticket = getUrlParam('ticket');
        if(ticket){
          goHome && (window.location.replace((userInfo && userInfo.homePath) || PageEnum.BASE_HOME));
        }else{
          goHome && (await router.replace((userInfo && userInfo.homePath) || PageEnum.BASE_HOME));
        }
        // goHome && (await router.replace(userInfo && userInfo.homePath || PageEnum.BASE_HOME));
      }
      return data;
    },
    /**
     * 手机号登录
     * @param params
     */
    async phoneLogin(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await phoneLoginApi(loginParams, mode);
        const { token } = data;
        // save token
        this.setToken(token);
        return this.afterLoginAction(goHome,data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    /**
     * 获取用户信息
     */
    async getUserInfoAction(): Promise<UserInfo | null> {
      if (!this.getToken) {
        return null;
      }
      const { userInfo, sysAllDictItems } = await getUserInfo();

      if (userInfo) {
        const { roles = [] } = userInfo;
        if (isArray(roles)) {
          const roleList = roles.map((item) => item.value) as RoleEnum[];
          this.setRoleList(roleList);
        } else {
          userInfo.roles = [];
          this.setRoleList([]);
        }
        this.setUserInfo(userInfo);
        this.setTenant(userInfo?.tenantId)
      }
      /**
       * 添加字典信息到缓存
       * @updateBy:lsq
       * @updateDate:2021-09-08
       */
      if(sysAllDictItems) {
        this.setAllDictItems(sysAllDictItems);
      }

      const [
        sysConfigResult,
        fieldsColumnsConfigResult,
        tenantDisabledDictItemsResult,
        tenantFieldConfigListResult,
        userConfigResult,
      ] = await Promise.allSettled([
        getServerSysConfig(),
        getUserFieldColumnsConfig(),
        queryTenantDisabledDictItemsApi(),
        queryFieldConfigListApi({
          tenantId: userInfo?.tenantId,
        }),
        getUserConfig(),
      ]);

      if (sysConfigResult.status === 'fulfilled' && sysConfigResult.value) {
        this.setServerSysConfig(sysConfigResult.value);
      }

      if (fieldsColumnsConfigResult.status === 'fulfilled' && fieldsColumnsConfigResult.value) {
        this.setFieldsColumnsConfig(fieldsColumnsConfigResult.value);
      } else {
        this.setFieldsColumnsConfig({});
      }

      if (
        tenantDisabledDictItemsResult.status === 'fulfilled' &&
        tenantDisabledDictItemsResult.value
      ) {
        this.setTenantDisabledDictItems(tenantDisabledDictItemsResult.value);
      }

      if (tenantDisabledDictItemsResult.status === 'rejected') {
        console.log(tenantDisabledDictItemsResult.reason);
      }

      if (
        tenantFieldConfigListResult.status === 'fulfilled' &&
        tenantFieldConfigListResult.value
      ) {
        this.setTenantFieldConfigList(tenantFieldConfigListResult.value);
      }

      if (tenantFieldConfigListResult.status === 'rejected') {
        console.log(tenantFieldConfigListResult.reason);
      }

      if (userConfigResult.status === 'fulfilled') {
        this.setUserConfig(userConfigResult.value);
      }

      return userInfo;
    },
    /**
     * 退出登录
     */
    async logout(goLogin = false,userClickLogout = false) {
      if (this.getToken) {
        try {
          await doLogout();
          trackOnlineTimeBuryPoint();
          this.setIsWeakPassword(false);
          _MEIQIA('closeConversation',(msg) => {
            console.log(msg)
          })
          _MEIQIA('hidePanel');
          // window.location.reload()
        } catch {
          console.log('注销Token失败');
        }
      }
      this.setToken('');
      setAuthCache(TOKEN_KEY, null);
      this.setSessionTimeout(false);
      this.setUserInfo(null);
      this.setLoginInfo(null);
      this.setUserConfig(null);
      this.setDrawerInfo(null);
      localStorage.removeItem(ANNOTATION_REVIEW_HINT_KEY);
      //如果开启单点登录,则跳转到单点统一登录中心
      const openSso = useGlobSetting().openSso;
      if(openSso=='true'){
        await useSso().ssoLoginOut();
      }

      let pathParams = {
        path: PageEnum.BASE_LOGIN,
      }
      if(goLogin && !userClickLogout){
        pathParams.query = {
          // 传入当前的路由，登录成功后跳转到当前路由
          redirect: router.currentRoute.value.fullPath,
        }
      }
      goLogin && (await router.push(pathParams));
    },
  /**
   * 登录事件
   */
   async ThirdLogin(
       params: ThirdLoginParams & {
           goHome?: boolean;
           mode?: ErrorMessageMode;
       },
      ): Promise<any | null> {
      try {
          const { goHome = true, mode, ...ThirdLoginParams } = params;
          const data = await thirdLogin(ThirdLoginParams, mode);
          const { token } = data;
          // save token
          this.setToken(token);
          return this.afterLoginAction(goHome,data);
      } catch (error) {
          return Promise.reject(error);
      }
   },
    /**
     * 退出询问
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      createConfirm({
        iconType: 'warning',
        title: t('sys.app.logoutTip'),
        content: t('sys.app.logoutMessage'),
        onOk: async () => {
          await this.logout(true, true);
        },
      });
    },
    /**
     * 接口返回code402跳转至注册(宣传)页面
     */
     toRegister () {
      this.setToken('');
      setAuthCache(TOKEN_KEY, null);
      this.setSessionTimeout(false);
      this.setUserInfo(null);
      this.setLoginInfo(null);
      router.push(PageEnum.REGISTER)
    }
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}

